// ============================================================
// MobileNumberField.tsx — B4c (D4): the pieces every "Mobile (for e-signature
// codes)" field shares (client card, litigation friend card, witness card):
//   • MobileInput — the input in the card's edit form: FormField + a tel box,
//     the hint, and the error (FieldError) linked to the box with
//     aria-describedby; the value as typed (utils/mobileNumber normalises).
//   • MobileValue — the read view's value: "07700 900123" (a tel: link) or
//     "Not recorded".
//   • MobileChangedHint — "Last changed by Sarah Jones on 31 October 2026"
//     from the subject's history (masked server-side; shown only once there
//     is history). Fails quietly — the history is a nicety, not the record.
// Shown only while the firm's e-signature pilot is on (hooks/useEsignPilot) —
// each caller checks.
// ============================================================
import React, { useEffect, useState } from 'react'
import FormField from '../shared/FormField'
import FieldError from '../shared/FieldError'
import PhoneLink from '../shared/PhoneLink'
import { inpCls } from './clientInfoRegistry'
import { formatUkMobile, normaliseUkMobile } from '../../utils/mobileNumber'
import type { MobileChange } from '../../types/mobileNumber.types'
import { CODES_MOBILE_LABEL } from '../../utils/esignMobile'

// (E-sign fixes B) The agreed wording (utils/esignMobile CODES_MOBILE_LABEL).
export const MOBILE_LABEL = CODES_MOBILE_LABEL
export const MOBILE_HINT = 'A UK mobile. Signing codes are texted to this number.'

// The local check on Save: the message, or null when it's fine (blank clears).
export const mobileInputError = (typed: string): string | null => {
  const n = normaliseUkMobile(typed)
  return n.ok ? null : n.error
}
// Typed value → what's stored (E.164 or null); unparseable → null (callers check first).
export const mobileE164Of = (typed: string): string | null => {
  const n = normaliseUkMobile(typed)
  return n.ok ? n.e164 : null
}

export const MobileInput: React.FC<{
  id: string
  value: string
  onChange: (v: string) => void
  error?: string
  disabled?: boolean
  // The card's own input classes (the client cards use inpCls; billing cards INPUT).
  className?: string
  // (E-sign fixes B) Another label / hint (e.g. "Leave blank to keep using the main phone").
  label?: string
  hint?: React.ReactNode
  placeholder?: string
}> = ({ id, value, onChange, error, disabled, className, label = MOBILE_LABEL, hint = MOBILE_HINT, placeholder = '07700 900123' }) => {
  const errId = `${id}-err`
  const hintId = `${id}-hint`
  return (
    <FormField variant="info" label={label} htmlFor={id}>
      <input
        id={id}
        type="tel"
        inputMode="tel"
        autoComplete="off"
        maxLength={30}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${errId} ${hintId}` : hintId}
        className={`${className ?? inpCls(!!error)} tabular-nums${className && error ? ' !border-casevo-danger' : ''}`}
      />
      <span id={errId}><FieldError message={error} /></span>
      <p id={hintId} className="m-0 mt-1 text-[12px] text-casevo-text-soft">{hint}</p>
    </FormField>
  )
}

export const MobileValue: React.FC<{ e164: string | null | undefined; className?: string; empty?: React.ReactNode }> = ({ e164, className, empty }) =>
  e164
    ? <PhoneLink value={formatUkMobile(e164)} className={className} plainClassName="tabular-nums" />
    : <>{empty ?? <span className="text-casevo-text-soft">Not recorded</span>}</>

const fmtLong = (iso: string): string => {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
}
// "Last changed by Sarah Jones on 31 October 2026" (or null).
export const lastChangedText = (rows: MobileChange[] | null | undefined): string | null => {
  const last = rows?.[0]
  if (!last) return null
  const when = fmtLong(last.changedAt)
  return `Last changed${last.changedBy ? ` by ${last.changedBy}` : ''}${when ? ` on ${when}` : ''}`
}

// Loads the history (again whenever `refreshKey` changes — e.g. after a save).
export const MobileChangedHint: React.FC<{ load: () => Promise<MobileChange[]>; refreshKey?: string | number; className?: string }> = ({ load, refreshKey, className }) => {
  const [rows, setRows] = useState<MobileChange[] | null>(null)
  useEffect(() => {
    let live = true
    load().then((r) => { if (live) setRows(r) }).catch(() => { if (live) setRows(null) })
    return () => { live = false }
    // `load` is a fresh closure each render; the subject + refreshKey decide.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey])
  const text = lastChangedText(rows)
  if (!text) return null
  return <p className={`m-0 mt-1 text-[12px] text-casevo-text-soft ${className ?? ''}`} data-testid="mobile-last-changed">{text}</p>
}
